import './App.css'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import LazyImage from './LazyImage'

function BirdingBlog (): JSX.Element {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const images = [
    'https://cdn.eqsong.com/eqsong-images/A26I4582 copy.jpg',
    'https://cdn.eqsong.com/eqsong-images/582288842645192306.jpg'
  ]

  const nextImage = (): void => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length)
  }

  const prevImage = (): void => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length)
  }

  return (
    <div className="page blog-page">
      <nav className="page-nav">
        <Link to="/" className="btn btn-secondary">← Back to Home</Link>
      </nav>

      <section className="page-header">
        <h1>🦅 Red-headed Woodpecker: A Master of Flight and Insect Hunting</h1>
        <p>The Redheaded Woodpecker catches flying insect on the wing - Birding Trip Blog Series 1</p>
        <div className="blog-meta">
          <span>Published: May 23, 2023</span>
          <span>Category: Birding</span>
          <span>Location: National Wildlife Refuge at Cedar Point, OH</span>
        </div>
      </section>

      <div className="blog-container">
        <article className="blog-article">
          <section className="blog-section">
            <h2>Introduction</h2>
            <p>
              In the realm of the avian world, few creatures captivate our attention as effortlessly as the Redheaded Woodpecker. 
              With its striking plumage and impressive aerial acrobatics, this charismatic bird is a true marvel of nature. Join me as 
              we delve into the National Wildlife Refuge at Cedar Point, OH, the home of a Redheaded Woodpecker, and discover how it 
              masters the art of flight and insect hunting during our birding trip in May 23, 2023.
            </p>
          </section>

          <section className="blog-section">
            <h2>A Distinguished Appearance</h2>
            <p>
              The Redheaded Woodpecker (Melanerpes erythrocephalus) is easily recognizable due to its vibrant plumage. As the name 
              suggests, its distinguishing feature is a brilliant red head that contrasts sharply against its white belly and black 
              back. This bold coloration makes the Redheaded Woodpecker a stunning sight amidst the surrounding foliage.
            </p>
          </section>

          <section className="blog-section">
            <h2>Photography Gallery</h2>
            <div className="image-carousel">
              <button className="carousel-btn prev" onClick={prevImage}>❮</button>
              <div className="carousel-container">
                <LazyImage src={images[currentImageIndex]} alt={`Woodpecker ${currentImageIndex + 1}`} className="blog-image carousel-image" />
                <div className="carousel-counter">{currentImageIndex + 1} / {images.length}</div>
              </div>
              <button className="carousel-btn next" onClick={nextImage}>❯</button>
            </div>
            <div className="carousel-indicators">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
          </section>

          <section className="blog-section">
            <h2>Flight Expertise</h2>
            <p>
              One of the Redheaded Woodpecker's most remarkable attributes is its agility in flight. It possesses strong wings and a 
              sleek, aerodynamic body that allows it to maneuver swiftly and precisely through the air. Observing this woodpecker in 
              flight is truly a spectacle; it effortlessly weaves between branches, executes intricate aerial displays, and performs 
              daring mid-air turns.
            </p>
            <p>
              The Redheaded Woodpecker has a voracious appetite for insects, and it has evolved a range of hunting techniques to catch 
              its preferred prey. These woodpeckers are known for their ability to catch insects on the wing, displaying impressive aerial 
              skills. With its keen eyesight and lightning-fast reflexes, the Redheaded Woodpecker can spot insects in mid-flight and 
              swiftly intercept them, snatching them out of the air with its powerful bill.
            </p>
            <p>
              In particular, the Redheaded Woodpecker can adjust its flight patterns in terms of speed and posture. As shown in the above 
              images, it slowed down a little bit and changed its flight postures from horizontal to almost vertical ones to catch the 
              insect at this particular situation. Interestingly, it also popped up its eye protection layer at moment of impact as a 
              precautionary measure.
            </p>
          </section>

          <section className="blog-section">
            <h2>Insect Hunting Techniques</h2>
            <p>
              The Redheaded Woodpecker employs several sophisticated hunting strategies to secure its meals. These include:
            </p>
            <ul>
              <li>Aerial interception: Catching insects in mid-flight with precision and speed</li>
              <li>Flight pattern adjustment: Modifying speed and posture for different prey types</li>
              <li>Eye protection: Deploying protective membranes during impact moments</li>
              <li>Ground gleaning: Searching for insects on the ground and tree bark</li>
            </ul>
          </section>

          <section className="blog-section">
            <h2>Conservation Status and Importance</h2>
            <p>
              While the Redheaded Woodpecker was once a common sight, its population has experienced a decline in recent years. Habitat 
              loss, degradation, and competition from non-native species have contributed to its declining numbers. Recognizing the 
              significance of this species, conservation efforts are underway to protect its habitats, raise awareness, and ensure its 
              long-term survival.
            </p>
          </section>

          <section className="blog-section">
            <h2>Conclusion</h2>
            <p>
              The Redheaded Woodpecker, with its striking appearance and extraordinary flight abilities, serves as a testament to the 
              wonders of nature. Its prowess in insect hunting, both in flight and through bark-drilling techniques, showcases the 
              remarkable adaptability and resourcefulness of this species. As we marvel at the Redheaded Woodpecker's aerial displays 
              and observe its skilled insect-catching techniques, let us also recognize the importance of conserving its habitats and 
              ensuring a future where this beautiful bird continues to grace our woodlands and inspire awe in all who encounter it.
            </p>
          </section>

          <div className="related-content">
            <h3>Related Blog Posts</h3>
            <div className="related-blogs">
              <Link to="/blog/ai" className="related-blog-card">
                <span>📝 AI Fundamentals - Part I</span>
              </Link>
              <Link to="/blog/eagles" className="related-blog-card">
                <span>🦅 Conowingo Dam Eagles</span>
              </Link>
              <Link to="/gallery" className="related-blog-card">
                <span>📸 Photo Gallery</span>
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}

export default BirdingBlog
